import SlideFrame from "./_SlideFrame";
import { fmt, PRICING } from "@/lib/api";

const competitors = [
  { name: "Nelnet Diversified Services", focus: "Diversified consumer, student, auto", range: "75–150", min: "$10k–$25k", revShare: false },
  { name: "Systems & Services Tech. (SST)", focus: "Consumer, auto RISC, fintech", range: "80–140", min: "$7.5k–$20k", revShare: false },
  { name: "Portfolio Financial Services (PFSC)", focus: "Consumer, equipment, auto", range: "90–150", min: "$10k–$15k", revShare: false },
  { name: "Launch Servicing", focus: "Auto RISC, near-prime / subprime", range: "75–125", min: "$5k–$15k", revShare: false },
  { name: "Vervent", focus: "Consumer, auto, solar, fintech", range: "85–140", min: "$10k–$20k", revShare: false },
  { name: "Turnstile Capital Mgmt", focus: "Consumer, student, charged-off", range: "100–175", min: "$7.5k", revShare: false },
  { name: "Westlake Portfolio Mgmt", focus: "Subprime auto RISC, lease", range: "75–125", min: "$10k", revShare: false },
  { name: "ACI / LoanPro platform only", focus: "Tech only — you hire the operators", range: "50–100 + tech fees", min: "$3k–$10k", revShare: false },
];

export default function SlideCompetitive() {
  // IBA implied bps at default illustrative portfolio
  const ibaLow = 29;   // at $12,500 avg loan
  const ibaHigh = 48;  // at $7,500 avg loan

  return (
    <SlideFrame tag="04 · Economics">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
        <div>
          <p className="overline mb-4">Competitive landscape</p>
          <h2 className="font-display text-5xl md:text-6xl tracking-tighter leading-[0.95] font-medium max-w-4xl">
            How we stack up <em className="italic text-primary">against the market</em>.
          </h2>
        </div>
        <p className="text-sm text-muted-foreground max-w-sm">
          Industry benchmarks for performing unsecured consumer loan servicing, sourced from
          public materials, SEC filings, and market estimates.
        </p>
      </div>

      {/* Headline positioning */}
      <div className="bg-white border-t-4 border-t-primary border-x border-b border-foreground/20 p-8 md:p-10 mb-10">
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <p className="overline mb-2">Market midpoint</p>
            <p className="font-mono-data text-5xl md:text-6xl tabular text-foreground">~100 bps</p>
            <p className="text-sm text-muted-foreground mt-2">75–150 bps typical range</p>
          </div>
          <div className="md:border-l md:border-foreground/15 md:pl-6">
            <p className="overline mb-2 flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 bg-primary" />
              IBA Servicing implied bps
            </p>
            <p className="font-mono-data text-5xl md:text-6xl tabular text-primary">
              {ibaLow}–{ibaHigh}<span className="text-2xl"> bps</span>
            </p>
            <p className="text-sm text-muted-foreground mt-2">at $3/loan/mo on prime & near-prime</p>
          </div>
          <div className="md:border-l md:border-foreground/15 md:pl-6">
            <p className="overline mb-2">You save vs midpoint</p>
            <p className="font-mono-data text-5xl md:text-6xl tabular text-primary">
              60–70<span className="text-2xl">%</span>
            </p>
            <p className="text-sm text-muted-foreground mt-2">before any revenue-share returned</p>
          </div>
        </div>
      </div>

      {/* Competitor table */}
      <div className="border border-foreground/20 bg-white">
        <div className="grid grid-cols-12 gap-4 px-5 py-3 bg-muted border-b border-foreground/20 text-[11px] uppercase tracking-brand font-bold">
          <span className="col-span-4">Competitor</span>
          <span className="col-span-4 hidden md:inline">Primary focus</span>
          <span className="col-span-4 md:col-span-2 text-right">Typical bps</span>
          <span className="col-span-4 md:col-span-2 text-right">Min / month</span>
        </div>
        {competitors.map((c) => (
          <div key={c.name} className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-border items-baseline">
            <span className="col-span-12 md:col-span-4 font-display text-lg">{c.name}</span>
            <span className="col-span-12 md:col-span-4 text-xs text-muted-foreground">{c.focus}</span>
            <span className="col-span-6 md:col-span-2 text-right font-mono-data text-sm">{c.range}</span>
            <span className="col-span-6 md:col-span-2 text-right font-mono-data text-xs text-muted-foreground">{c.min}</span>
          </div>
        ))}
        {/* IBA row — highlighted as the positive standout */}
        <div className="grid grid-cols-12 gap-4 px-5 py-5 bg-muted border-t-2 border-primary items-baseline">
          <span className="col-span-12 md:col-span-4 font-display text-xl text-foreground">
            <span className="inline-block w-1.5 h-1.5 bg-primary mr-2 align-middle" />
            IBA Servicing
          </span>
          <span className="col-span-12 md:col-span-4 text-xs text-muted-foreground">
            Flat $3/loan — servicing + call center + optional compliance + 50/50 revenue share
          </span>
          <span className="col-span-6 md:col-span-2 text-right font-mono-data text-base text-foreground font-semibold">{ibaLow}–{ibaHigh} bps</span>
          <span className="col-span-6 md:col-span-2 text-right font-mono-data text-xs text-muted-foreground">
            {fmt(PRICING.monthly_minimum)}
          </span>
        </div>
      </div>

      <p className="mt-8 text-sm text-muted-foreground italic font-display text-xl max-w-3xl">
        We publish this on purpose. Ask any competitor for their real effective bps after minimum fees — the conversation gets quiet fast.
      </p>
    </SlideFrame>
  );
}
